import TagManager from "react-gtm-module";
import { v4 as uuidv4 } from "uuid";
import { UAParser } from "ua-parser-js";

const parser = new UAParser();
const eventQueue = [];
let authReady = false;

/* ------------------------------------------------------------------ */
/*  Tag Manager initialization                                        */
/* ------------------------------------------------------------------ */
export const initTagManager = () => {
  TagManager.initialize({
    gtmId: process.env.REACT_APP_GOOGLE_TAG_MANAGER_ID,
    auth: process.env.REACT_APP_GOOGLE_TAG_MANAGER_AUTH,
    preview: process.env.REACT_APP_GOOGLE_TAG_MANAGER_PREVIEW,
  });
};

/* ------------------------------------------------------------------ */
/*  Persistent identifiers                                            */
/* ------------------------------------------------------------------ */

/** Unique browser/device ID (persists across sessions) */
export const getDeviceId = () => {
  const existing = localStorage.getItem("device_id");
  if (existing) return existing;

  const newId = uuidv4();
  localStorage.setItem("device_id", newId);
  return newId;
};

export const PLATFORM_TYPE = process.env.REACT_APP_PLATFORM_TYPE || "web";

const enrichParams = (params = {}) => ({
  device_id: getDeviceId(),
  device_type: parser.getOS().name,
  platform_type: PLATFORM_TYPE,
  ...params,
});

/* ------------------------------------------------------------------ */
/*  Internal state and queue                                          */
/* ------------------------------------------------------------------ */

/** Flush queued events once user is identified */
const flushQueue = () => {
  if (!eventQueue.length) return;
  eventQueue.forEach(({ event, params }) => {
    safePush({ event, ...enrichParams(params) });
  });
  eventQueue.length = 0;
};

/* ------------------------------------------------------------------ */
/*  Core tracking methods                                             */
/* ------------------------------------------------------------------ */

const safePush = (data) => {
  try {
    TagManager.dataLayer({ dataLayer: data });
  }
  catch (err) {
    console.warn("GTM push blocked:", err);
  }
};

export const logUserIdentified = (userId) => {
  if (!userId) return;
  try {
    safePush({
      event: "user_identified",
      ...enrichParams(),
      crm_id: userId,
    });
  } catch (error) {
    console.error("Error logging user_identified:", error);
  }
};

/** Identify current user and flush queued events */
export const identifyUser = (userId) => {
  authReady = true;
  logUserIdentified(userId);
  flushQueue();
};

export const resetAnalyticsState = () => {
  authReady = false;
};

/** Generic event logger (queues until user is known) */
export const logEvent = (event, params = {}) => {
  if (authReady) {
    safePush({ event, ...enrichParams(params) });
  } else {
    eventQueue.push({ event, params });
  }
};

export const logLoginEvent = (method = "unknown") => {
  try {
    logEvent("login", { method });
  } catch (error) {
    console.error("Error logging login:", error);
  }
};

export const logPageView = (params = {}) => {
  try {
    logEvent("page_view", params);
  } catch (error) {
    console.error("Error logging page_view:", error);
  }
};

export const logSidebarNavClick = ({ id, label, path, parentId = null }) => {
  try {
    logEvent("click_sidebar_nav", {
      menu_id: id,
      menu_label: label,
      menu_path: path ?? null,
      submenu_parent: parentId,
    });
  } catch (error) {
    console.error("Error logging click_sidebar_nav:", error);
  }
};
